import { useEffect } from "react";
import { getFocusableElements } from "./focusUtils";

interface Options {
  submenuRef: React.RefObject<HTMLDivElement>;
  showSubmenu: boolean;
  onClose: () => void;
  parentButtonRef: React.RefObject<HTMLElement>;
}

export const useSubmenuKeyboardNav = ({
  submenuRef,
  showSubmenu,
  onClose,
  parentButtonRef,
}: Options) => {
  useEffect(() => {
    if (!showSubmenu || !submenuRef.current) return;

    const container = submenuRef.current;
    const focusableEls = getFocusableElements(container);
    if (focusableEls.length === 0) return;

    // eslint-disable-next-line prefer-destructuring
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        parentButtonRef.current?.focus();
      }

      if (e.key !== "Tab") return;

      // Shift+Tab on first element → return to parent button
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        onClose();
        parentButtonRef.current?.focus();
      }

      // Tab on last element → close submenu and move to next <li>
      if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        onClose();

        const currentListItem = parentButtonRef.current?.closest("li");
        const nextListItem = currentListItem?.nextElementSibling as HTMLElement;
        const nextButton = nextListItem?.querySelector<HTMLElement>(
          "a, button, [tabindex]:not([tabindex='-1'])",
        );
        nextButton?.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [submenuRef, showSubmenu, onClose, parentButtonRef]);
};
